"use client";

import { useEffect, useState } from "react";
import { useSession } from "@/lib/auth/client";
import type { Locale } from "@/lib/i18n/config";
import type { Dictionary } from "@/lib/i18n/get-dictionary";
import { BadgeChip, formatNewJobsBadgeLabel } from "./new-jobs-badge-chip";
import { NewJobsBadgeGuest } from "./new-jobs-badge-guest";

type Props = {
	locale: Locale;
	t: Dictionary;
};

/**
 * Red-dot "new jobs since last visit" chip in the navbar wordmark.
 *
 * MUST stay client-only. This renders inside the shared `(public)` layout on
 * EVERY route. It previously read `getSafeSession()` (→ `headers()`) on the
 * server, which forced the whole route dynamic — fine for force-dynamic pages,
 * but it made the static ISR job-detail page throw "Page changed from static to
 * dynamic at runtime" → HTTP 500 on every job page (incident 2026-05-30).
 * Reading auth client-side via `useSession()` keeps the shared layout free of
 * server-side request APIs so pages can render statically.
 *
 * Guests → {@link NewJobsBadgeGuest} (localStorage-based). Authenticated users →
 * fetch the count from /api/me/new-jobs-count (server resolves it from the
 * user's `lastFeedSeenAt`). Returns null when there's nothing to show.
 */
export function NewJobsBadge({ locale, t }: Props) {
	const { data: session, isPending } = useSession();
	const authed = !!session?.user;
	const [count, setCount] = useState(0);
	const [ready, setReady] = useState(false);

	useEffect(() => {
		if (isPending || !authed) return;
		let cancelled = false;
		fetch("/api/me/new-jobs-count", {
			method: "GET",
			credentials: "include",
			cache: "no-store",
			headers: { Accept: "application/json" },
		})
			.then((r) =>
				r.ok ? (r.json() as Promise<{ count?: number }>) : { count: 0 },
			)
			.then((d) => {
				if (cancelled) return;
				setCount(typeof d.count === "number" ? d.count : 0);
				setReady(true);
			})
			.catch(() => {
				if (!cancelled) setReady(true);
			});
		return () => {
			cancelled = true;
		};
	}, [authed, isPending]);

	if (isPending) return null;
	if (!authed) return <NewJobsBadgeGuest locale={locale} t={t} />;
	if (!ready || count <= 0) return null;

	const numDisplay = count > 99 ? "99+" : String(count);
	const aria =
		locale === "en"
			? `${numDisplay} new jobs since your last visit`
			: `${numDisplay} lowongan baru sejak kunjungan terakhirmu`;
	return (
		<BadgeChip
			label={formatNewJobsBadgeLabel(count, t, locale)}
			aria={aria}
			href={`/${locale}/jobs?posted=24h#feed`}
			count={count}
		/>
	);
}
